<template>
  <div class="doc-work">
    <!-- 页头 -->
    <div class="page-head">
      <div class="page-head-left">
        <h2 class="page-title">工作台</h2>
        <p class="page-desc">标书业务全景，洞察投标与中标态势</p>
      </div>
      <div class="page-head-right">
        <span class="update-tip"><i class="el-icon-time"></i> 数据更新至 {{ updatedAt }}</span>
        <el-button type="primary" plain icon="el-icon-refresh" @click="handleRefresh">刷新数据</el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-grid">
      <div v-for="card in statCards" :key="card.key" class="stat-card" :class="'tone-' + card.tone">
        <div class="stat-icon"><i :class="card.icon"></i></div>
        <div class="stat-main">
          <div class="stat-label">{{ card.label }}</div>
          <div class="stat-value">
            <span :ref="'stat-' + card.key" class="stat-num">{{ card.display }}</span>
            <span v-if="card.unit" class="stat-unit">{{ card.unit }}</span>
          </div>
          <div class="stat-sub">{{ card.sub }}</div>
        </div>
        <div class="stat-deco"></div>
      </div>
    </div>

    <!-- 图表区 -->
    <div class="chart-grid">
      <div class="panel">
        <div class="panel-head">
          <h3>标书状态分布</h3>
          <span class="panel-note">按开标结果统计</span>
        </div>
        <div class="panel-body">
          <div ref="pieChart" class="chart chart-lg"></div>
        </div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3>每月投标与中标趋势</h3>
          <span class="panel-note">按开标日期统计</span>
        </div>
        <div class="panel-body">
          <div ref="lineChart" class="chart chart-lg"></div>
        </div>
      </div>
    </div>

    <!-- 近期项目 -->
    <div class="panel">
      <div class="panel-head">
        <h3>近期投标项目</h3>
        <span class="panel-note">共 {{ total }} 条 · 展示最新 8 条</span>
      </div>
      <div class="panel-body">
        <el-table :data="recentBids" stripe style="width: 100%" @row-click="goList">
          <el-table-column prop="序号" label="序号" width="70" align="center">
            <template slot-scope="scope">
              <span class="seq-num">{{ scope.row['序号'] }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="项目名称" label="项目名称" min-width="260" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="project-name">{{ scope.row['项目名称'] }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="采购单位" label="采购单位" min-width="220" show-overflow-tooltip>
          </el-table-column>
          <el-table-column prop="开标日期" label="开标日期" width="150">
            <template slot-scope="scope">
              <span class="date-cell">{{ scope.row['开标日期'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="报价" label="报价" width="130" align="right">
            <template slot-scope="scope">
              <span class="price-cell">{{ formatMoney(scope.row['报价']) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="是否中标" label="结果" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="bidStatusType(scope.row['是否中标'])" effect="light" size="mini">
                {{ bidStatusLabel(scope.row['是否中标']) }}
              </el-tag>
            </template>
          </el-table-column>
          <template slot="empty">
            <div class="table-empty"><i class="el-icon-document"></i><p>暂无投标项目</p></div>
          </template>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { RAW_DATA, formatMoney, bidStatusType, bidStatusLabel, monthlyStats } from './bidData'

export default {
  name: 'DocumentWork',
  data() {
    return {
      rawData: RAW_DATA,
      updatedAt: '',
      pieChartInst: null,
      lineChartInst: null,
      total: 0,
      wonCount: 0,
      pendingCount: 0,
      totalAmount: 0
    }
  },
  computed: {
    statCards() {
      const rate = this.total > 0 ? ((this.wonCount / this.total) * 100).toFixed(1) : '0'
      return [
        { key: 'total', label: '投标项目总数', icon: 'el-icon-folder-opened', value: this.total, display: String(this.total), unit: '', sub: '2024-2025 年度累计', tone: 'blue' },
        { key: 'won', label: '中标项目', icon: 'el-icon-trophy', value: this.wonCount, display: String(this.wonCount), unit: '', sub: '中标率 ' + rate + '%', tone: 'green' },
        { key: 'pending', label: '进行中项目', icon: 'el-icon-time', value: this.pendingCount, display: String(this.pendingCount), unit: '', sub: '待开标 / 待定', tone: 'orange' },
        { key: 'amount', label: '投标总金额', icon: 'el-icon-money', value: this.totalAmount, display: this.totalAmount.toFixed(1), unit: '万元', sub: '按最高限价统计', tone: 'purple' }
      ]
    },
    recentBids() {
      return this.rawData.slice(-8).reverse()
    }
  },
  created() {
    this.updatedAt = this.formatNow()
  },
  mounted() {
    this.calcStats()
    this.$nextTick(() => {
      this.renderPie()
      this.renderLine()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    formatNow() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes())
    },
    calcStats() {
      const data = this.rawData
      this.total = data.length
      this.wonCount = data.filter(d => d['是否中标'] === '是').length
      const lostCount = data.filter(d => d['是否中标'] === '否').length
      this.pendingCount = this.total - this.wonCount - lostCount
      this.totalAmount = data.reduce((s, d) => {
        const v = parseFloat(d['最高限价（预算价）万元'])
        return isNaN(v) ? s : s + v
      }, 0)
      this.$nextTick(() => this.animateCards())
    },
    animateCards() {
      this.statCards.forEach(card => {
        const el = this.$refs['stat-' + card.key]
        if (!el || !el[0]) return
        const decimals = card.key === 'amount' ? 1 : 0
        const target = card.value
        const duration = 900
        const start = performance.now()
        const tick = now => {
          const progress = Math.min((now - start) / duration, 1)
          const eased = 1 - Math.pow(1 - progress, 3)
          const val = target * eased
          el[0].textContent = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString()
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    },
    renderPie() {
      if (this.pieChartInst) this.pieChartInst.dispose()
      const el = this.$refs.pieChart
      if (!el) return
      const won = this.wonCount
      const lost = this.rawData.filter(d => d['是否中标'] === '否').length
      const pending = this.pendingCount
      const rate = this.total > 0 ? (won / this.total * 100).toFixed(1) : '0'
      this.pieChartInst = echarts.init(el)
      this.pieChartInst.setOption({
        color: ['#36cf63', '#fa8c16', '#c0c4cc'],
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 项 ({d}%)'
        },
        title: {
          text: rate + '%',
          subtext: '中标率',
          left: 'center',
          top: '38%',
          textStyle: { fontSize: 26, fontWeight: 700, color: '#1f2d3d' },
          subtextStyle: { fontSize: 12, color: '#909399' }
        },
        legend: {
          bottom: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: { color: '#606266', fontSize: 12 }
        },
        series: [{
          type: 'pie',
          radius: ['58%', '76%'],
          center: ['50%', '44%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 3 },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 16, fontWeight: 600, formatter: '{b}\n{c} 项' }
          },
          data: [
            { value: won, name: '已中标' },
            { value: lost, name: '未中标' },
            { value: pending, name: '待定' }
          ]
        }]
      })
    },
    renderLine() {
      if (this.lineChartInst) this.lineChartInst.dispose()
      const el = this.$refs.lineChart
      if (!el) return
      const stats = monthlyStats(this.rawData)
      const labels = stats.map(s => s.month)
      this.lineChartInst = echarts.init(el)
      this.lineChartInst.setOption({
        color: ['#409eff', '#36cf63'],
        tooltip: { trigger: 'axis' },
        legend: {
          right: 0,
          top: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: { color: '#606266', fontSize: 12 }
        },
        grid: { left: 12, right: 16, top: 36, bottom: 8, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: { color: '#909399' }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: { lineStyle: { color: '#f0f2f5' } },
          axisLabel: { color: '#909399' }
        },
        series: [
          {
            name: '投标总数',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            data: stats.map(s => s.total),
            lineStyle: { width: 2.5 },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64,158,255,0.25)' },
                { offset: 1, color: 'rgba(64,158,255,0)' }
              ])
            }
          },
          {
            name: '中标数',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            data: stats.map(s => s.won),
            lineStyle: { width: 2.5 },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(54,207,99,0.22)' },
                { offset: 1, color: 'rgba(54,207,99,0)' }
              ])
            }
          }
        ]
      })
    },
    handleResize() {
      if (this.pieChartInst) this.pieChartInst.resize()
      if (this.lineChartInst) this.lineChartInst.resize()
    },
    disposeCharts() {
      if (this.pieChartInst) { this.pieChartInst.dispose(); this.pieChartInst = null }
      if (this.lineChartInst) { this.lineChartInst.dispose(); this.lineChartInst = null }
    },
    handleRefresh() {
      this.updatedAt = this.formatNow()
      this.calcStats()
      this.renderPie()
      this.renderLine()
      this.$message({ type: 'success', message: '数据已刷新' })
    },
    goList() {
      this.$message.info('可在「标书列表」中查看全部项目')
    },
    formatMoney,
    bidStatusType,
    bidStatusLabel
  }
}
</script>

<style scoped lang="scss">
.doc-work {
  padding: 4px 0 20px;
}

/* 页头 */
.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
  letter-spacing: 0.5px;
}
.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}
.page-head-right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.update-tip {
  font-size: $font-size-base;
  color: #909399;
}
.update-tip i {
  margin-right: 4px;
}

/* 统计卡片 */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 20px;
}
.stat-card {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 20px 22px;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.08);
}
.stat-deco {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}
.tone-blue .stat-deco { background: linear-gradient(90deg, #409eff, #79bbff); }
.tone-green .stat-deco { background: linear-gradient(90deg, #36cf63, #85e0a3); }
.tone-orange .stat-deco { background: linear-gradient(90deg, #fa8c16, #ffb36b); }
.tone-purple .stat-deco { background: linear-gradient(90deg, #722ed1, #ad7be9); }

.tone-blue .stat-icon { background: #e6f2ff; color: #409eff; }
.tone-green .stat-icon { background: #e9f9ef; color: #36cf63; }
.tone-orange .stat-icon { background: #fff3e6; color: #fa8c16; }
.tone-purple .stat-icon { background: #f3ecfb; color: #722ed1; }

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
  margin-right: 16px;
}
.stat-main { flex: 1; min-width: 0; }
.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 6px;
}
.stat-value {
  display: flex;
  align-items: baseline;
  line-height: 1;
}
.stat-num {
  font-size: 28px;
  font-weight: 700;
  color: #1f2d3d;
  font-variant-numeric: tabular-nums;
}
.stat-unit {
  font-size: 13px;
  color: #909399;
  margin-left: 4px;
}
.stat-sub {
  margin-top: 8px;
  font-size: $font-size-base;
  color: #b0b6c0;
}

/* 面板 */
.panel {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  margin-bottom: 20px;
  overflow: hidden;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid #f2f3f5;
}
.panel-head h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}
.panel-head h3::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background: #409eff;
  margin-right: 8px;
  vertical-align: -2px;
}
.panel-note {
  font-size: $font-size-base;
  color: #b0b6c0;
}
.panel-body { padding: 18px 22px 8px; }

.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.chart-lg { height: 300px; }

/* 近期项目表格 */
.seq-num {
  color: #909399;
  font-variant-numeric: tabular-nums;
}
.project-name {
  color: #1f2d3d;
  font-weight: 500;
}
.date-cell {
  color: #606266;
  font-size: $font-size-base;
}
.price-cell {
  color: #1f2d3d;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.table-empty {
  padding: 30px 0;
  color: #c0c4cc;
  font-size: 13px;
}
.table-empty i {
  font-size: 34px;
  display: block;
  margin-bottom: 8px;
}

@media (max-width: 1200px) {
  .stat-grid { grid-template-columns: repeat(2, 1fr); }
  .chart-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .stat-grid { grid-template-columns: 1fr; }
}
</style>
